import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, CreditCard, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import lawpayService from '../../services/lawpayService';

const RecentPaymentsWidget = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await lawpayService.getTransactions({ limit: 8 });
      setTransactions(response.data?.transactions || response.data || []);
    } catch (error) {
      console.error('Error fetching recent payments:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const getTransactionIcon = (type) => {
    if (type === 'refund') return <ArrowDownRight className="h-4 w-4 text-red-500" />;
    return <ArrowUpRight className="h-4 w-4 text-green-500" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-100 rounded"></div>
            <div className="h-12 bg-gray-100 rounded"></div>
            <div className="h-12 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <CreditCard className="h-5 w-5 text-green-600 mr-2" />
          Recent Payment Receipts
          {transactions.length > 0 && (
            <span className="ml-2 bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              {transactions.length}
            </span>
          )}
        </h3>
      </div>
      <div className="p-6">
        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium">No recent payments</p>
            <p className="text-gray-500 text-sm">Payment receipts will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.slice(0, 6).map((txn) => (
              <div
                key={txn.id}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {getTransactionIcon(txn.transaction_type)}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {txn.client_name || 'Unknown Client'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {txn.transaction_type === 'refund' ? 'Refund' : 'Payment'} &middot; {formatDate(txn.created_at)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${txn.transaction_type === 'refund' ? 'text-red-600' : 'text-green-600'}`}>
                    {txn.transaction_type === 'refund' ? '-' : '+'}{formatAmount(txn.amount)}
                  </p>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${getStatusBadge(txn.status)}`}>
                    {txn.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentPaymentsWidget;
